import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  ChevronDown,
  ChevronRight,
  X,
  RefreshCw,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  activationsApi,
  type ScheduledActivation,
  type ActivationExecution,
  type ActivationStatus,
} from "@/services/api/agentApi";
// User-facing label + description for each known action type
const ACTION_LABELS: Record<string, { title: string; description: string }> = {
  send_communication: {
    title: "Send a message",
    description: "SMS, email, or push notification",
  },
  device_control: {
    title: "Control a smart device",
    description: "Kasa on/off/toggle",
  },
  fetch_and_report: {
    title: "Run a query and send result",
    description: "Data query → delivered to you",
  },
  project_deadline_reminder: {
    title: "Task deadline reminder",
    description: "Notify assignee before deadline",
  },
  project_status_checkin: {
    title: "Project status check-in",
    description: "Summary to project owner",
  },
  project_escalation_check: {
    title: "Escalation check",
    description: "Watch for unresolved issues",
  },
  check_workflow_status: {
    title: "Check workflow status",
    description: "Poll workflow engine",
  },
  execute_data_query: {
    title: "Run a data query",
    description: "For a project task",
  },
  create_task_workflow: {
    title: "Build a task workflow",
    description: "Generate workflow for task",
  },
  deliver_inbound: {
    title: "Deliver message",
    description: "Route message to recipient agent",
  },
  mission_heartbeat: {
    title: "Mission heartbeat",
    description: "Internal mission health check",
  },
  mission_step: {
    title: "Mission step",
    description: "Execute next step in a mission",
  },
  escalate_message: {
    title: "Message escalation",
    description: "Retry on next channel",
  },
  deliver_pending: {
    title: "Flush pending messages",
    description: "Deliver queued messages",
  },
  agent_wake_task: {
    title: "Wake agent and run task",
    description: "Direct agent task dispatch",
  },
};

function actionLabel(action?: string): { title: string; description: string } {
  if (!action)
    return { title: "Agent task", description: "Send message to agent" };
  return (
    ACTION_LABELS[action] ?? { title: action, description: "Custom action" }
  );
}

function statusBadgeVariant(
  status: ActivationStatus,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "pending":
    case "active":
      return "default";
    case "completed":
      return "secondary";
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
}

function humanizeRecurrence(a: ScheduledActivation): string {
  if (a.activation_type === "one_shot") return "One time";
  if (a.cron_expression) return `Schedule: ${a.cron_expression}`;
  if (a.interval_ms) return `Every ${formatDuration(a.interval_ms)}`;
  return "Recurring";
}

function formatDuration(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const days = Math.round(hr / 24);
  return `${days}d`;
}

function formatAbsolute(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function formatRelative(iso?: string): string {
  if (!iso) return "";
  const diffMs = new Date(iso).getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const suffix = diffMs >= 0 ? "from now" : "ago";
  return `${formatDuration(absMs)} ${suffix}`;
}

function formatActiveHours(a: ScheduledActivation): string | null {
  const ah = a.active_hours;
  if (!ah) return null;
  const days = ah.days?.length ? ah.days.join(", ") : "every day";
  return `${ah.start}–${ah.end} ${ah.timezone} (${days})`;
}

interface ExpandedPanelProps {
  activation: ScheduledActivation;
}

const ExpandedPanel: React.FC<ExpandedPanelProps> = ({ activation }) => {
  const [execs, setExecs] = useState<ActivationExecution[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    activationsApi
      .listExecutions(activation.activation_id, 25)
      .then((res) => {
        if (!cancelled) setExecs(res.data ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error("Could not load execution history");
          setExecs([]);
        }
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activation.activation_id]);

  const activeHours = formatActiveHours(activation);

  return (
    <div className="mt-3 space-y-3 rounded-md border bg-muted/30 p-4 text-sm">
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            Reason
          </div>
          <div>
            {activation.reason || (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            Next fire
          </div>
          <div>
            {formatAbsolute(activation.next_fire_at)}{" "}
            <span className="text-muted-foreground">
              ({formatRelative(activation.next_fire_at)})
            </span>
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            Recurrence
          </div>
          <div>{humanizeRecurrence(activation)}</div>
        </div>
        {activeHours && (
          <div>
            <div className="text-xs font-medium text-muted-foreground">
              Active hours
            </div>
            <div>{activeHours}</div>
          </div>
        )}
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            Fire count
          </div>
          <div>
            {activation.fire_count}
            {activation.max_fires
              ? ` / ${activation.max_fires}`
              : activation.activation_type === "recurring"
                ? " (no limit)"
                : ""}
          </div>
        </div>
        {activation.last_fired_at && (
          <div>
            <div className="text-xs font-medium text-muted-foreground">
              Last fired
            </div>
            <div>
              {formatAbsolute(activation.last_fired_at)}{" "}
              <span className="text-muted-foreground">
                ({formatRelative(activation.last_fired_at)})
              </span>
            </div>
          </div>
        )}
      </div>

      {activation.activation_type === "recurring" && !activation.max_fires && (
        <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            This recurring task has no fire limit. It will continue until
            cancelled.
          </span>
        </div>
      )}

      <div>
        <div className="mb-2 text-xs font-medium text-muted-foreground">
          Recent executions
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : !execs || execs.length === 0 ? (
          <div className="text-muted-foreground">No executions yet</div>
        ) : (
          <ul className="space-y-1">
            {execs.map((e) => (
              <li
                key={e.execution_id}
                className="flex items-start gap-2 rounded border bg-background p-2"
              >
                {e.status === "success" && (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                )}
                {e.status === "failure" && (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                )}
                {e.status === "deferred" && (
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium">
                      {formatAbsolute(e.fired_at)}
                    </span>
                    <span className="text-muted-foreground">
                      attempt {e.attempt_number}
                      {typeof e.duration_ms === "number"
                        ? ` · ${e.duration_ms}ms`
                        : ""}
                    </span>
                  </div>
                  {e.result_summary && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {e.result_summary}
                    </div>
                  )}
                  {e.error_message && (
                    <div className="mt-0.5 break-words text-xs text-red-700">
                      {e.error_message}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

interface ScheduledActivitiesCardProps {
  orgSlug?: string;
}

export const ScheduledActivitiesCard: React.FC<
  ScheduledActivitiesCardProps
> = ({ orgSlug }) => {
  const [loading, setLoading] = useState(true);
  const [activations, setActivations] = useState<ScheduledActivation[]>([]);
  const [failures, setFailures] = useState<ActivationExecution[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [filter, setFilter] = useState<
    "active" | "completed" | "cancelled" | "all"
  >("active");

  const fetchAll = useCallback(async () => {
    if (orgSlug) {
      setActivations([]);
      setFailures([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [listRes, failRes] = await Promise.all([
        activationsApi.list(),
        activationsApi.recentFailures(24),
      ]);
      setActivations(listRes.data ?? []);
      setFailures(failRes.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Could not load scheduled activities");
    } finally {
      setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleCancel = useCallback(
    async (id: string) => {
      if (orgSlug) return;
      if (!confirm("Cancel this scheduled activity?")) return;
      setCancelling(id);
      try {
        await activationsApi.cancel(id);
        toast.success("Activity cancelled");
        await fetchAll();
      } catch (err) {
        console.error(err);
        toast.error("Could not cancel activity");
      } finally {
        setCancelling(null);
      }
    },
    [fetchAll, orgSlug],
  );

  const filtered = useMemo(() => {
    const byFilter = (a: ScheduledActivation) => {
      if (filter === "all") return true;
      if (filter === "active")
        return (
          a.status === "pending" ||
          a.status === "active" ||
          a.status === "paused"
        );
      if (filter === "completed") return a.status === "completed";
      if (filter === "cancelled")
        return a.status === "cancelled" || a.status === "failed";
      return true;
    };
    return activations.filter(byFilter);
  }, [activations, filter]);

  const failureCountByActivation = useMemo(() => {
    const map = new Map<string, number>();
    for (const f of failures) {
      map.set(f.activation_id, (map.get(f.activation_id) ?? 0) + 1);
    }
    return map;
  }, [failures]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Scheduled Activities
            </CardTitle>
            <CardDescription>
              Tasks your agent has scheduled on your behalf
            </CardDescription>
          </div>
          {!orgSlug && (
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchAll}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {orgSlug ? (
          <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
            Organization-scoped scheduled activities are not available yet.
            Personal scheduled activities are hidden on organization settings.
          </div>
        ) : (
          <>
            {failures.length > 0 && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-medium">
                    {failures.length} failure{failures.length === 1 ? "" : "s"}{" "}
                    in the last 24 hours
                  </div>
                  <div className="text-xs text-red-800">
                    Expand affected activities below to see error details.
                  </div>
                </div>
              </div>
            )}

            <div className="mb-3 flex gap-2">
              {(["active", "completed", "cancelled", "all"] as const).map(
                (f) => (
                  <Button
                    key={f}
                    variant={filter === f ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilter(f)}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ),
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No {filter === "all" ? "" : filter} scheduled activities
              </div>
            ) : (
              <ul className="space-y-2">
                {filtered.map((a) => {
                  const action =
                    typeof a.context.action === "string"
                      ? a.context.action
                      : undefined;
                  const label = actionLabel(action);
                  const expanded = expandedId === a.activation_id;
                  const recentFailures =
                    failureCountByActivation.get(a.activation_id) ?? 0;
                  return (
                    <li
                      key={a.activation_id}
                      className="rounded-lg border bg-background p-3"
                    >
                      <div className="flex items-start gap-3">
                        <button
                          className="mt-0.5 text-muted-foreground hover:text-foreground"
                          onClick={() =>
                            setExpandedId(expanded ? null : a.activation_id)
                          }
                          aria-label={expanded ? "Collapse" : "Expand"}
                        >
                          {expanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{label.title}</span>
                            <Badge
                              variant={statusBadgeVariant(a.status)}
                              className="text-xs"
                            >
                              {a.status}
                            </Badge>
                            {recentFailures > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {recentFailures} recent failure
                                {recentFailures === 1 ? "" : "s"}
                              </Badge>
                            )}
                          </div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {a.reason || label.description}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                            <span>Next: {formatAbsolute(a.next_fire_at)}</span>
                            <span>· {humanizeRecurrence(a)}</span>
                            <span>· Fired {a.fire_count}×</span>
                          </div>
                        </div>
                        {(a.status === "pending" ||
                          a.status === "active" ||
                          a.status === "paused") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(a.activation_id)}
                            disabled={cancelling === a.activation_id}
                            title="Cancel"
                          >
                            {cancelling === a.activation_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                      {expanded && <ExpandedPanel activation={a} />}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
